import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChatEntry } from '@/components/livekit/chat/chat-entry';
import { ProductCarousel } from '@/components/livekit/chat/product-carousel';
import { cn } from '@/lib/utils';
const ChatEntryMotion = motion.create(ChatEntry);
const ProductCarouselMotion = motion.create(ProductCarousel);
export function Transcript({ ref, messages, agentState, productEvents, onAddToCart, className, }) {
    const isAgentThinking = agentState === 'thinking';
    const isAgentSpeaking = agentState === 'speaking';
    const transcriptRef = useRef(null);
    const handleRef = useCallback((node) => {
        transcriptRef.current = node;
        if (ref) {
            if (typeof ref === 'function') {
                ref(node);
            }
            else {
                ref.current = node;
            }
        }
    }, [ref]);
    // Per-message status for LOCAL (user) messages:
    //  - 'seen'     — an agent message appears after it, OR agent is currently
    //                 responding to it (thinking/speaking) after the last user msg.
    //  - 'delivered'— otherwise (the agent hasn't acknowledged yet).
    // Agent messages always get `undefined` so no ticks render on them.
    const statusByIndex = useMemo(() => {
        var _a, _b;
        const agentAfter = new Array(messages.length).fill(false);
        let seen = false;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (!((_a = messages[i].from) === null || _a === void 0 ? void 0 : _a.isLocal)) {
                seen = true;
            }
            agentAfter[i] = seen;
        }
        // "Currently responding" counts as seen for the trailing user message.
        const lastIdx = messages.length - 1;
        const lastIsUser = lastIdx >= 0 && ((_b = messages[lastIdx].from) === null || _b === void 0 ? void 0 : _b.isLocal);
        const agentEngaged = isAgentThinking || isAgentSpeaking;
        return messages.map((m, i) => {
            var _a;
            if (!((_a = m.from) === null || _a === void 0 ? void 0 : _a.isLocal))
                return undefined;
            if (agentAfter[i])
                return 'seen';
            if (lastIsUser && i === lastIdx && agentEngaged)
                return 'seen';
            return 'delivered';
        });
    }, [messages, isAgentThinking, isAgentSpeaking]);
    // Merge chat messages and product carousels into one chronological timeline
    // so a search result's carousel renders right after the agent's "here are
    // a few options" reply rather than at the bottom of the transcript.
    const timeline = useMemo(() => {
        const items = messages.map((m, i) => ({
            kind: 'message',
            ts: m.timestamp,
            key: m.id,
            data: m,
            status: statusByIndex[i],
        }));
        for (const ev of productEvents !== null && productEvents !== void 0 ? productEvents : []) {
            items.push({
                kind: 'carousel',
                ts: ev.timestamp,
                key: ev.id,
                data: ev,
            });
        }
        items.sort((a, b) => a.ts - b.ts);
        return items;
    }, [messages, statusByIndex, productEvents]);
    // Auto-scroll to the bottom on any of:
    //  - new / removed message
    //  - typing indicator appearing or disappearing
    //  - the chat container itself resizing (e.g., popup open animation)
    useEffect(() => {
        function scrollToBottom() {
            const el = transcriptRef.current;
            if (el)
                el.scrollTop = el.scrollHeight;
        }
        if (!transcriptRef.current)
            return;
        const resizeObserver = new ResizeObserver(scrollToBottom);
        resizeObserver.observe(transcriptRef.current);
        // Defer one frame so a just-rendered typing bubble is measured before we scroll.
        const id = requestAnimationFrame(scrollToBottom);
        return () => {
            cancelAnimationFrame(id);
            resizeObserver.disconnect();
        };
    }, [messages, isAgentThinking]);
    return (_jsx("div", { ref: handleRef, className: cn('scrollbar-on-hover flex grow flex-col overflow-x-hidden overflow-y-auto bg-gray-50 px-4 py-4', className), children: _jsx("div", { className: "flex flex-1 flex-col justify-end gap-3 pt-8", children: _jsxs(AnimatePresence, { children: [timeline.map((item) => item.kind === 'message' ? (_jsx(ChatEntryMotion, { hideName: true, initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: 'auto' }, exit: { opacity: 1, height: 'auto', translateY: 0.001 }, transition: { duration: 0.5, ease: 'easeOut' }, entry: item.data, status: item.status }, item.key)) : (_jsx(ProductCarouselMotion, { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 }, transition: { duration: 0.4, ease: 'easeOut' }, products: item.data.products, onAddToCart: onAddToCart }, item.key))), isAgentThinking && (_jsx(motion.div, { initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: 'auto' }, exit: { opacity: 0, height: 0 }, transition: { duration: 0.3, ease: 'easeOut' }, className: "flex flex-col gap-0.5", children: _jsx("span", { className: "mr-auto max-w-[75%] rounded-lg bg-[#7C3AED] px-4 py-3", children: _jsxs("span", { className: "flex items-center gap-[5px]", children: [_jsx("span", { className: "typing-dot" }), _jsx("span", { className: "typing-dot" }), _jsx("span", { className: "typing-dot" })] }) }) }, "agent-typing"))] }) }) }));
}
